import { PayloadAction } from '@reduxjs/toolkit';
import { CancelTokenSource } from 'axios';
import { update } from 'core/actions/admin/data';
import { signedUrlRequest } from 'core/services/admin';
import { uploadRequest } from 'core/services/asset';
import { UploadTypes } from 'types/app/upload';
import { IKeyArray } from 'types/IKeyArray';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { IProcessUpdate, ISocketMessage, SocketMethods } from './websocket';
import { EntityTypes } from 'constants/entities';

export interface IUploadReducer {
  uploads: IKeyArray<IUploadFile>;
}

export interface IUploadFile {
  id: string;
  ref: string;
  filename: string;
  contentType: string;
  size: number;
  state: UploadState;
  signedUrl?: string;
  progress?: number;
  created: number;
  folderId: number;
  parentType?: EntityTypes;
  parentId?: number;
  sceneId?: number;
  sceneConfigId?: number;
  moduleId?: number;
  index?: number;
  cancelToken?: string;
  uploadType: UploadTypes;
  update?: IProcessUpdate;
  entity?: IProcessUpdate;
}

export enum UploadState {
  DELETED = -1,
  WAITING = 0,
  UPLOADING = 1,
  UPLOADED = 2,
  ERROR = 3,
  PROCESSING = 4,
  PROCESSED = 5,
}

const initialState: IUploadReducer = {
  uploads: {},
};

const uploadSlice = createSlice({
  //@ts-ignore
  name: 'upload',
  initialState,
  reducers: {
    upload(state, action: PayloadAction<IUploadFile>) {},
    cancelUpload(state, action: PayloadAction<string>) {
      state.uploads[action.payload].state = UploadState.DELETED;
    },
    uploadStarted(state, action: PayloadAction<Partial<IUploadFile>>) {
      state.uploads[action.payload.id].state = UploadState.UPLOADING;
    },
    uploadProgress(state, action: PayloadAction<Partial<IUploadFile>>) {
      state.uploads[action.payload.id].progress = action.payload.progress;
    },
    uploadProcessing(state, action: PayloadAction<ISocketMessage>) {
      if (action.payload.method === SocketMethods.PROCESSING) {
        if (!state.uploads[action.payload.uploadId]) state.uploads[action.payload.uploadId] = {};

        state.uploads[action.payload.uploadId].state = UploadState.PROCESSING;
        state.uploads[action.payload.uploadId] = action.payload;
      }
    },
    uploadScene(state, action: PayloadAction<{ uploadId: string; sceneId: number }>) {
      state.uploads[action.payload.uploadId].sceneId = action.payload.sceneId;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(signedUrlRequest.pending, (state: IUploadReducer, action) => {
        let index = state.uploads ? Object.values(state.uploads).length : 0;
        for (let file of action.meta.arg.files) {
          state.uploads[file.id] = { ...file, index: index++, created: new Date().getTime() };
        }
      })
      .addCase(signedUrlRequest.rejected, (state: IUploadReducer, action) => {})
      .addCase(signedUrlRequest.fulfilled, (state: IUploadReducer, action) => {
        for (let file of action.payload.files) state.uploads[file.id].signedUrl = file.signedUrl;
      })
      .addCase(uploadRequest.pending, (state: IUploadReducer, action) => {
        if (state.uploads[action.meta.arg.id]) state.uploads[action.meta.arg.id].state = UploadState.UPLOADING;
      })
      .addCase(uploadRequest.rejected, (state: IUploadReducer, action) => {
        if (state.uploads[action.meta.arg.id] && state.uploads[action.meta.arg.id].state !== UploadState.DELETED)
          state.uploads[action.meta.arg.id].state = UploadState.ERROR;
      })
      .addCase(uploadRequest.fulfilled, (state: IUploadReducer, action) => {
        if (state.uploads[action.meta.arg.id]) state.uploads[action.meta.arg.id].state = UploadState.UPLOADED;
      })
      .addCase(update, (state: IUploadReducer, action) => {
        if (
          'uploadId' in action.payload &&
          typeof action.payload.uploadId === 'string' &&
          state.uploads[action.payload.uploadId]
        )
          state.uploads[action.payload.uploadId].state = UploadState.PROCESSED;
      });
  },
});

export const { actions: uploadActions, reducer: uploadReducer } = uploadSlice;

export default uploadSlice.reducer;
